import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DraftKind, FlagId, PiiIgnoreEntry, TicketDetail as TicketDetailType } from '../types';
import { formatBackendError } from '../utils/formatError';
import { smarttix } from '../services/api';
import { theme } from '../theme';
import { SECONDARY_AXIS_MAX_VISIBLE } from '../constants';
import { ArrivalAge } from './ArrivalAge';
import { BentoCard, FilterPanel, SelectionToggleIcon, RestoreIcon, SpreadsheetRowIcon, CircleIcon, TicketRowMeta, PopoutIcon, HeaderIconButton, HandlingStripFace, primaryBandStyle, ServedFace, formFilterCountOf } from './shared';
import { formatRowDate } from '../utils/dateFormatting';
import { AutoBar, Lever } from './AutoButton';
import { archivAutoAction } from '../utils/autoAction';
import { RueckgaengigToast } from './RueckgaengigToast';
import { CountUpNumber } from './CountUpNumber';
import { useSelectionMode } from '../hooks/useSelectionMode';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { useArchivedPagination } from '../hooks/useArchivedPagination';
import { ArchiveBucket, ARCHIVE_BUCKETS, isArchiveBucket, bucketForRestoredStatus, restoredTicketToast } from '../services/lifecycleBuckets';
import { useTicketFacets } from '../hooks/useTicketFacets';
import { attributeFilterParams, withAttributeValues, hasAttributeFilter, type AttributeFilter } from '../services/ticketAttributes';
import { pillFilterParams } from '../services/searchFaceIds';
import { DateRangeValue } from './DateRangeInput';
import { listDetailFlex } from '../services/listDetailRatio';
import TicketDetailComponent from './TicketDetail';
import { openTicketPopout } from './PopoutShell';
import { onTicketMutated, announceTicketMutated } from '../services/ticketSync';
import { PageType } from './NavBar';

// Bucket semantics (server-side, mirrors the StatistikSeite archive-mode KPI split):
//   Erledigt        = approved or sent      (rep approved, then archived)
//   Liegengeblieben = suggestion_ready       (AI processed, rep abandoned)
//   Ungeklärt       = pipeline_error OR KU OR unprocessed (never classified / unresolvable)
// The FE key IS 'ungeklaert' — the same spelling the API's bucket param and the served `bucket`
// face key use (lifecycleBuckets.ts, Task R2 part 4c) — no translation left anywhere. Row→bucket
// assignment is the BE's job.

/*
<purpose>Read-only archive browser with restore. Prevents ticket loss after the undo toast expires.</purpose>
<dependencies>useArchivedPagination → smarttix.getArchivedPage (server-side bucket/filter/cursor + truthful counts); smarttix.getTicket; smarttix.unarchiveTicket + bulk endpoints.</dependencies>
<constraints>No polling. First page on mount; reload after restore/auto actions. Infinite scroll via IntersectionObserver sentinel. No approve/reject/reanalysis actions.</constraints>
*/

interface ArchivSeiteProps {
  onNavigate: (page: PageType, params?: Record<string, string>) => void;
  // Tenant Eingangsweg visibility — same value the Antworten host and the pop-out receive, so the
  // one TicketDetail component cannot render differently depending on which page mounted it.
  showSourceChannel?: boolean;
  // Tenant-wide inline-image counting rule, threaded from App's single /config read — a per-host
  // config read is forbidden, so this arrives as a prop like showSourceChannel above.
  attachmentsCountInline?: boolean;
}

// The ledger's axis strip (walk fix round 2, 2026-09-09) — the SAME width the active list's strip
// reserves, so the two lists read as one idiom at two row heights. That width is theme.stripWidth
// keyed on the mode (operator ruling 2026-09-16); the ledger only paints a strip in strip_abbr, so
// in practice it takes the full-width entry — but it takes it from the shared token rather than a
// number of its own, or the two lists drift apart the day the token moves.
// Fixed and pre-allocated either way: the Kürzel inside it arrives with the tenant's vocabulary, and
// a column that sized itself to its content would re-flow every row the moment /config landed.

const COMPACT_BREAKPOINT = 900;
// Includes BentoCard's own hover transition (box-shadow/transform) — BentoCard spreads `...style` last,
// so a bare `transition: flex` here would clobber the card-lift transition.
const MORPH_TRANSITION = 'flex 0.45s cubic-bezier(0.4,0,0.2,1), box-shadow 180ms ease-out, transform 180ms ease-out';

// The bucket tab shimmer's width, per key (fix round 1) — "Liegengeblieben" and "Erledigt" are not
// close to the same footprint at this tab's 13px text, and a shimmer sized for one word either
// clips short of the other's real word or sits comically wide next to it once it lands (UI Layout
// Stability Rule cuts both ways). Sizes a PLACEHOLDER only — it spells no word itself, so it is not
// a second code-owned copy of the vocabulary the badge-fact ratchet guards against. Measured at
// ~7.1px/char for this font/weight/size, same basis the single shared width (64) used before.
const ARCHIVE_TAB_SHIMMER_WIDTH: Record<ArchiveBucket, number> = {
  erledigt: 58,
  liegengeblieben: 108,
  ungeklaert: 64,
};

const ArchivSeite: React.FC<ArchivSeiteProps> = ({ onNavigate, showSourceChannel, attachmentsCountInline }) => {
  // The list header names the tenant's own axis, read from the same seam the rows use —
  // otherwise the header says one word and the badges beneath it say another.
  const cm = useCategoryMeta();
  // Whether the ledger's rows carry the axis strip. Read ONCE here rather than per row, and only
  // meaningful because the list refuses to paint rows at all until config settles (see the settle
  // hold at the rows branch below) — so this is never the seeded default answering for the tenant.
  const axisStrip = cm.rowLayout === 'strip_abbr';
  // ─── State ───────────────────────────────────────────
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetailType | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // ─── Bucket filter chip + selection mode (Plan #2) ──
  // A stored/URL value from before this part ('unklassifiziert', or the older 'fehlgeschlagen'
  // alias) is simply not a recognised ArchiveBucket any more — isArchiveBucket rejects it and this
  // opens on the default tab, never an empty or unknown one (Review Focus 4, no translation arm).
  const [bucket, setBucket] = useState<ArchiveBucket>(() => {
    const url = new URL(window.location.href);
    const v = url.searchParams.get('archive');
    return isArchiveBucket(v) ? v : 'erledigt';
  });
  const updateBucket = (next: ArchiveBucket) => {
    const url = new URL(window.location.href);
    url.searchParams.set('archive', next);
    window.history.replaceState({}, '', url.toString());
    setBucket(next);
    // A selection belongs to the list it was made in (walk fix round 4, 2026-09-09) — leaving it
    // standing left one bucket listed beside a detail pane reading a row from another. Cleared
    // HERE rather than in an effect on `bucket`: this is the one place a bucket changes, and the
    // clear is part of the same act.
    setSelectedUuid(null);
    setSelectedTicket(null);
  };
  const sel = useSelectionMode();
  const [removingUuids, setRemovingUuids] = useState<Set<string>>(new Set());
  // Per-row in-flight restore (single-restore button). Local now that ArchivSeite
  // no longer routes through tm.archivedTickets / tm.restoringUuids.
  const [restoringUuids, setRestoringUuids] = useState<Set<string>>(new Set());
  const [undoToast, setUndoToast] = useState<{ count: number; verb: string; uuids: string[]; undo: 'archive' } | null>(null);
  const [infoToast, setInfoToast] = useState<string | null>(null);
  const [autoSubmitting, setAutoSubmitting] = useState(false);
  // Bucket switch clears selection — different rows visible.
  useEffect(() => { if (sel.mode) sel.exitMode(); }, [bucket]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Detail pop-out ─────────────────────────────────
  // Maximizing is a per-SECTION act inside TicketDetail now (Part C wave 2, 2026-09-09) — the
  // whole-pane stage this header used to raise is retired, so nothing overlay-shaped is left for
  // this page to own. The pop-out is unchanged: it hands the ticket to its own window.
  const popoutDetail = useCallback(() => {
    if (selectedUuid) openTicketPopout(selectedUuid);
  }, [selectedUuid]);

  // Filters — all server-side now (no client-side useMemo). Date range is ISO from/to.
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [reasonCodeFilter, setReasonCodeFilter] = useState<string[]>([]);
  // Classified-alongside ATTRIBUTE filters (wim vocabulary v7, 2026-09-13) — the ledger narrows by
  // the same served attributes the active list does; see AntwortenSeite for why the state is one
  // record keyed by served name.
  const [attributeFilter, setAttributeFilter] = useState<AttributeFilter>({});
  // Sender-identity registry (P2, 2026-09-13), Task R2 — see AntwortenSeite's own field for why
  // this is separate from attributeFilter.
  const [senderFilter, setSenderFilter] = useState<string[]>([]);
  // part 7, 2026-10-11: the pill filter — the „Warnhinweis" flags (OR within). Sent through pillFilterParams with the served search ids (searchFaceIds).
  const [flagFilter, setFlagFilter] = useState<FlagId[]>([]);
  // part 7 fix loop, 2026-10-12: the „Formular" facet — see AntwortenSeite's own field.
  const [hasFormFilter, setHasFormFilter] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterUser, setFilterUser] = useState('');
  const [dateRange, setDateRange] = useState<DateRangeValue>({ from: null, to: null });

  // Debounce the free-text search so each keystroke doesn't refetch a page.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(id);
  }, [search]);

  // ─── Server-side data (rows / counts / cursor) ───────
  const archived = useArchivedPagination(bucket, useMemo(() => ({
    search: debouncedSearch,
    primaryAxis: categoryFilter,
    secondaryAxis: reasonCodeFilter,
    from: dateRange.from,
    to: dateRange.to,
    uploadedBy: filterUser,
    senderOrganisationId: senderFilter,
    ...attributeFilterParams(attributeFilter),
    ...pillFilterParams({ flagFilter, hasFormFilter, search: debouncedSearch }, cm),
  }), [debouncedSearch, categoryFilter, reasonCodeFilter, dateRange.from, dateRange.to, filterUser, senderFilter, attributeFilter, flagFilter, hasFormFilter, cm]));

  // ─── Filter facets (Task R2, sender-identity registry, 2026-09-13) ──
  // Twin of AntwortenSeite's own — see useTicketFacets for the debounce/ordering/unsupported
  // posture both pages share. `bucket` IS the wire spelling now (Task R2, part 4c) — no translation
  // left between this call and the list's own (useArchivedPagination).
  const { facets, facetsUnsupported } = useTicketFacets(bucket, {
    search: search || undefined,
    primaryAxis: categoryFilter || undefined,
    secondaryAxis: reasonCodeFilter.length > 0 ? reasonCodeFilter : undefined,
    senderOrganisationId: senderFilter.length > 0 ? senderFilter : undefined,
    from: dateRange.from || undefined,
    to: dateRange.to || undefined,
    uploadedBy: filterUser || undefined,
    ...attributeFilterParams(attributeFilter),
    ...pillFilterParams({ flagFilter, hasFormFilter, search }, cm),
  });

  const rows = archived.rows;
  const erledigtCount = archived.counts.erledigt;
  const liegengebliebenCount = archived.counts.liegengeblieben;
  const ungeklaertCount = archived.counts.ungeklaert;
  // The „Formular" filter bubble's count — the visible archive bucket's served form-bearing subset
  // (part 7 fix loop, 2026-10-12); no count span where the list serves none.
  const formFilterCount = formFilterCountOf(archived.counts.formBearing, bucket, archived.loading);
  // Header + badge for the active bucket come from counts, not loaded-row length.
  const activeBucketCount = bucket === 'erledigt' ? erledigtCount
    : bucket === 'liegengeblieben' ? liegengebliebenCount
    : ungeklaertCount;

  // ─── Infinite-scroll sentinel ────────────────────────
  // Callback ref so the observer (re)attaches the moment the sentinel node mounts
  // or unmounts — a static useRef + mount-only effect would miss it (the sentinel
  // only renders once the first page has rows). loadMore self-guards on cursor.
  const loadMoreRef = useRef(archived.loadMore);
  loadMoreRef.current = archived.loadMore;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) loadMoreRef.current();
    });
    obs.observe(node);
    observerRef.current = obs;
  }, []);

  // Foreign-window mutations: silent ledger refresh + detail refetch when it's ours.
  // `archived` is recreated every render (new hook object) — a stable ref lets the
  // listener (subscribed once, [] deps) always call the CURRENT refresh(). Silent
  // (never flips loading, never collapses loaded pages) — reload() stays reserved
  // for direct user actions (restore/auto) that legitimately want a fresh page 1.
  const selectedUuidRef = useRef(selectedUuid);
  selectedUuidRef.current = selectedUuid;
  const archivedRef = useRef(archived);
  archivedRef.current = archived;
  useEffect(() => onTicketMutated(async (uuid) => {
    archivedRef.current.refresh();
    const sel = selectedUuidRef.current;
    if (sel && (uuid === sel || uuid === '*')) {
      const detail = await smarttix.getTicket(sel);
      // Guard: selection may have moved on to a different ticket while this
      // getTicket() was in flight — don't clobber the NOW-selected ticket's
      // detail with a stale response for the ticket the user left behind.
      if (selectedUuidRef.current === sel && detail) setSelectedTicket(detail);
    }
  }), []);

  // Responsive
  const [isCompact, setIsCompact] = useState(window.innerWidth < COMPACT_BREAKPOINT);
  useEffect(() => {
    const onResize = () => setIsCompact(window.innerWidth < COMPACT_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadDetail = useCallback(async (uuid: string) => {
    setDetailLoading(true);
    setError(null);
    try {
      const detail = await smarttix.getTicket(uuid);
      setSelectedTicket(detail);
    } catch {
      setSelectedTicket(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedUuid) loadDetail(selectedUuid);
    else setSelectedTicket(null);
  }, [selectedUuid, loadDetail]);

  // ─── Restore handler ────────────────────────────────
  const handleRestore = async (uuid: string) => {
    if (restoringUuids.has(uuid)) return;
    setActionLoading(true);
    setRestoringUuids(prev => new Set(prev).add(uuid));
    const ticket = rows.find(t => t.uuid === uuid);
    const res = await smarttix.unarchiveTicket(uuid);
    if (res.success) {
      // The tab a restored ticket returns to, not its raw pipeline status (operator ruling,
      // Task R2 part 4c, 2026-09-27) — the served bucket word, never a raw key: while the
      // vocabulary is still loading, cm.face cannot be trusted (fix round 1) so the toast
      // simply omits the tab clause (restoredTicketToast) rather than naming it in the
      // unresolved raw spelling. In practice this row is unreachable while cm.configLoading —
      // the SETTLE HOLD below paints no rows at all until config settles — but the fallback is
      // spelled out explicitly rather than relied on implicitly.
      const restoredBucket = bucketForRestoredStatus(ticket?.previousStatus);
      const bucketWord = cm.configLoading ? undefined : (cm.face('bucket', restoredBucket) ?? restoredBucket);
      // The toast owns its own read window (RueckgaengigToast, info mode) — this used to keep a
      // page-local timer beside a page-local copy of the toast markup (2026-09-08).
      setToast(restoredTicketToast(bucketWord));
      // Drift note: this per-uuid mutation calls smarttix directly rather than a tm.*
      // mutator (ArchivSeite doesn't route through TicketManagerContext — see file
      // header), so it misses the tm-chokepoint announce. Announce explicitly here;
      // a popout window on THIS uuid needs to know it left the archive.
      announceTicketMutated(uuid);
      setSelectedUuid(null);
      setSelectedTicket(null);
      // Refresh archive page (row leaves).
      archived.reload();
    } else {
      setError(res.error || 'Fehler beim Wiederherstellen');
    }
    setRestoringUuids(prev => { const next = new Set(prev); next.delete(uuid); return next; });
    setActionLoading(false);
  };

  // Uploader options for the filter — derived from currently-loaded rows. Server-side
  // counts are bucket-wide, but this dropdown only needs plausible suggestions; the
  // server applies the actual uploadedBy filter.
  const distinctUploaders = useMemo(() => {
    const emails = new Set(rows.map(t => t.uploadedByEmail).filter(Boolean) as string[]);
    return Array.from(emails).sort();
  }, [rows]);

  const hasActiveFilters = !!categoryFilter || reasonCodeFilter.length > 0 || !!search || !!filterUser || !!dateRange.from || !!dateRange.to || senderFilter.length > 0 || hasAttributeFilter(attributeFilter) || flagFilter.length > 0 || hasFormFilter;
  // Browse → list-dominant 2:1; reading a selected ticket → detail-dominant 1:2 (row layout only).
  const ratio = listDetailFlex(selectedUuid);
  // For returning focus to the open row when the detail is closed via the X (keyboard continuity).
  const listRef = useRef<HTMLDivElement>(null);
  // The detail pane's own focus target — the landmark a keyboard activation lands in. Same region
  // and the same wording as Antworten (operator ruling 2026-09-16): the ledger is read with the
  // keyboard as much as the active list is, and it was the one list that dropped the clerk nowhere.
  const detailRegionRef = useRef<HTMLDivElement>(null);
  // The tenant's PII ignore list, read for the SAME reason the pop-out reads it: the detail filters
  // the values it still lists against it, so the same archived ticket showed a different set of PII
  // findings in-page than in its own pop-out.
  const [piiIgnoreList, setPiiIgnoreList] = useState<PiiIgnoreEntry[]>([]);
  useEffect(() => { smarttix.getPiiIgnoreList().then(setPiiIgnoreList).catch(() => {}); }, []);
  // Kopieren on a PAST letter (operator ruling Q3 = A, 2026-09-17). A reference action, not a
  // decision: the clipboard write is the whole act. Nothing is CLAIMED here and nothing needs to
  // be refused either — `archived` is outside services/draftClaims' TRIAGE_DECIDABLE_STATUSES, so
  // the pop-out's handler already records nothing for these rows and this one matches it.
  // Returns whether the CLIPBOARD write happened — the card's own contract.
  const handleCopyDraft = useCallback(async (_kind: DraftKind, text: string): Promise<boolean> => {
    try { await navigator.clipboard.writeText(text); }
    catch { setError('Fehler beim Kopieren'); return false; }
    return true;
  }, []);
  const closeDetail = () => {
    const row = selectedUuid ? listRef.current?.querySelector<HTMLElement>(`[data-uuid="${selectedUuid}"]`) : null;
    setSelectedUuid(null);
    (row ?? listRef.current)?.focus();
  };

  // ─── Auto action (Plan #2) ──────────────────────────
  // ONE verb on this page, in every bucket: mass unarchive (bulkUnarchive). The rep restores, and
  // whatever they then decide is decided where decisions are made.
  // Liegengeblieben used to carry a second verb here — mark as Erledigt, which approved the ticket
  // and left it archived, saving a restore→approve→re-archive. Deactivated for ALL ROLES by
  // operator ruling (walk fix round 5, 2026-09-09) and REMOVED rather than disabled: an affordance
  // nobody may take is not an affordance, and core retires the endpoint in the same breath, so a
  // surviving caller would be a 404 waiting to be pressed. The archive does not approve; the route
  // is Wiederherstellen and then the ordinary approval in Offen.
  // Selection only operates on currently-loaded rows (server-side paginated set).
  const autoTargetUuids = sel.selected.size > 0
    ? rows.filter(t => sel.selected.has(t.uuid)).map(t => t.uuid)
    : [];
  const autoHasAction = autoTargetUuids.length > 0;
  // ⚠ SECONDARY SURFACE. Here an empty selection means NOTHING — on BOTH levers. That is the opposite
  // of the active list, where an empty selection under the excel lever means EVERY form-bearing ticket.
  // The asymmetry is deliberate: a mass download off the archive is not a gesture anyone asked for, and
  // the archive is where a careless "everything" would be largest. Do not unify the two pages.
  // The line, the button's face and the press read one table (utils/autoAction, archivAutoAction).
  const autoAction = archivAutoAction({ purpose: sel.purpose, picked: autoTargetUuids.length });

  // One button, one press; the active lever picks the verb. A quiet button never calls this.
  const handleAutoPress = () => {
    if (autoSubmitting || !autoHasAction) return;
    if (sel.purpose === 'excel') { handleDownloadXlsx(); return; }
    handleAutoUnarchive();
  };

  const handleAutoUnarchive = async () => {
    setAutoSubmitting(true);
    const uuids = autoTargetUuids.slice();
    setRemovingUuids(new Set(uuids));
    await new Promise(r => setTimeout(r, 200 + Math.min(40 * uuids.length, 2000)));
    try {
      const res = await smarttix.bulkUnarchive(uuids);
      if (res.error) {
        setError(res.error);
        setRemovingUuids(new Set());
      } else {
        setUndoToast({ count: res.unarchived, verb: 'wiederhergestellt', uuids, undo: 'archive' });
        announceTicketMutated('*');
        sel.exitMode();
        archived.reload();
        setRemovingUuids(new Set());
      }
    } finally {
      setAutoSubmitting(false);
    }
  };

  const handleAutoUndo = async () => {
    if (!undoToast) return;
    const { uuids, undo } = undoToast;
    setUndoToast(null);
    // 'archive' undoes a bulkUnarchive (re-archive the rows) — and it is the only undo this page
    // has, now that the approve-in-place capability is gone (round 5). Kept as a switch on `undo`
    // rather than collapsed into an unconditional call: the toast still carries the discriminator,
    // and a future second verb would add its arm here rather than rediscovering the shape.
    if (undo === 'archive') {
      await smarttix.bulkArchive(uuids);
      announceTicketMutated('*');
      archived.reload();
    }
  };

  // Why a download rather than a rendered table: the clerk's destination is Excel, and the workbook
  // carries per-cell notes (recognition confidence, where the two OCR passes disagreed, which values we
  // could not verify) that no HTML table would survive being pasted from. The row values are assembled
  // server-side, identically to the single-ticket copy, so the two delivery paths can never disagree
  // about what a cell says.
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const handleDownloadXlsx = async () => {
    if (xlsxBusy) return;
    const uuids = autoTargetUuids;
    if (uuids.length === 0) { setInfoToast('Keine Auswahl fuer den Excel-Download.'); return; }

    setXlsxBusy(true);
    try {
      // Archive page has ONE gesture — an explicit pick. It never means "everything in this view"
      // (see the selection comment above), so it always sends uuids and never a selection.
      const res = await smarttix.downloadFormRowsXlsx({ uuids });
      if (res.error || !res.blob) { setInfoToast(res.error || 'Excel-Download fehlgeschlagen.'); return; }

      // Tickets without a form extraction produce no row. Saying so is the whole point: a clerk who
      // selected 15 and received 12 must be told, not left to count rows in the workbook.
      const skipped = res.matched - res.delivered;
      if (skipped > 0) {
        setInfoToast(`${res.delivered} von ${res.matched} Vorgaengen konnten uebernommen werden. `
          + `${skipped} enthalten kein lesbares Formular.`);
      }

      const url = URL.createObjectURL(res.blob);
      const a = document.createElement('a');
      a.href = url;
      // The name comes from the client, which composed it from the rows the server actually
      // included. Hardcoding it here once made the rename inert: the api returned the right
      // name and the anchor ignored it.
      a.download = res.fileName ?? 'Notizblatt.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Released on the next tick rather than immediately: revoking synchronously can cancel the
      // download in some browsers before it has read the object.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } finally {
      setXlsxBusy(false);
    }
  };

  // ─── Render ─────────────────────────────────────────
  return (
    <div style={{ fontFamily: theme.font.family, animation: 'stx-fadeIn 0.3s ease' }}>
      {/* Page title row — count + selection + Auto controls (Plan #2). */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: theme.spacing.md,
        paddingBottom: theme.spacing.md,
        borderBottom: `1px solid ${theme.colors.borderLight}`,
        marginBottom: theme.spacing.lg,
        flexWrap: 'wrap',
      }}>
        <h1 style={{
          fontSize: 22, fontWeight: 600, color: theme.colors.navy,
          margin: 0, flex: 1, minWidth: 200,
        }}>
          {archived.loading || activeBucketCount === 0 ? 'Archiv' : `${activeBucketCount} archivierte Ticket${activeBucketCount !== 1 ? 's' : ''}`}
        </h1>
        <AutoBar page="archiv" action={autoAction} onPress={handleAutoPress} onClear={sel.clear} busy={autoSubmitting || xlsxBusy}>
          {rows.length > 0 && (
            <Lever on={sel.purpose === 'archive'} label={sel.purpose === 'archive' ? 'Auswahlmodus beenden' : 'Auswahlmodus starten'} onClick={() => sel.setPurpose('archive')}>
              <SelectionToggleIcon size={14} active={sel.purpose === 'archive'} />
            </Lever>
          )}
          {/* Form-to-excel lever, sibling of selection mode. Gated on the tenant capability first (the
              schema context, the rows' single source) — a role that does not own it never sees it. */}
          {cm.formExtractionEnabled && rows.length > 0 && (
            <Lever on={sel.purpose === 'excel'} label={sel.purpose === 'excel' ? 'Formular-Excel-Auswahl beenden' : 'Formularzeilen als Excel auswählen'} onClick={() => sel.setPurpose('excel')}>
              <SpreadsheetRowIcon size={14} active={sel.purpose === 'excel'} />
            </Lever>
          )}
        </AutoBar>
      </div>

      {/* 2-column layout */}
      <div style={{
        display: 'flex', gap: theme.spacing.lg,
        flexDirection: isCompact ? 'column' : 'row',
        ...(isCompact ? {} : { height: 'calc(100vh - 200px)', minHeight: 400 }),
      }}>
        {/* Left: archived ticket list */}
        <BentoCard
          title="Archivierte Tickets"
          style={{
            display: 'flex', flexDirection: 'column', minWidth: 0,
            flex: isCompact ? 1 : ratio.list,
            ...(isCompact ? { maxHeight: 500 } : { transition: MORPH_TRANSITION }),
          }}
          contentStyle={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, padding: 0 }}
        >
          <FilterPanel
            search={search}
            onSearchChange={setSearch}
            open={filterOpen}
            onOpenChange={setFilterOpen}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={setCategoryFilter}
            reasonCodeFilter={reasonCodeFilter}
            onReasonCodeFilterChange={setReasonCodeFilter}
            attributeFilter={attributeFilter}
            onAttributeFilterChange={(name, values) =>
              setAttributeFilter(prev => withAttributeValues(prev, name, values))}
            senderFilter={senderFilter}
            onSenderFilterChange={setSenderFilter}
            flagFilter={flagFilter}
            onFlagFilterChange={setFlagFilter}
            hasFormFilter={hasFormFilter}
            onHasFormFilterChange={setHasFormFilter}
            formFilterCount={formFilterCount}
            facets={facets}
            facetsUnsupported={facetsUnsupported}
            uploaders={distinctUploaders}
            filterUser={filterUser}
            onFilterUserChange={setFilterUser}
            from={dateRange.from}
            to={dateRange.to}
            onDateRangeChange={setDateRange}
            dateRangeLabel="Archiviert"
            onClearAll={() => {
              setSearch(''); setCategoryFilter(null);
              setReasonCodeFilter([]); setFilterUser(''); setDateRange({ from: null, to: null });
              setAttributeFilter({}); setSenderFilter([]);
              setFlagFilter([]); setHasFormFilter(false);
            }}
            hasActiveFilters={hasActiveFilters}
          />

          {/* Bucket tabs (below the search/filter bar, matches DashboardSeite.Verlauf pattern) */}
          <div style={{
            display: 'flex',
            borderBottom: `1px solid ${theme.colors.borderLight}`,
            marginBottom: theme.spacing.sm,
            userSelect: 'none',
          }}>
            {ARCHIVE_BUCKETS.map(b => {
              const active = bucket === b;
              const count = b === 'erledigt' ? erledigtCount : b === 'liegengeblieben' ? liegengebliebenCount : ungeklaertCount;
              return (
                <button key={b} onClick={() => updateBucket(b)}
                  title={cm.configLoading ? undefined : (cm.face('bucket', b) ?? b)}
                  style={{
                  flex: 1, minWidth: 0,
                  display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4,
                  padding: '8px 0', background: 'none',
                  border: 'none', borderBottom: `2px solid ${active ? theme.colors.navy : 'transparent'}`,
                  fontSize: 13, fontWeight: active ? 600 : 400,
                  color: active ? theme.colors.navy : theme.colors.textSecondary,
                  cursor: 'pointer', fontFamily: theme.font.family,
                }}>
                  {/* Label ellipsizes, count stays — keeps the tab one line so the list:detail morph doesn't reflow it. */}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    <ServedFace family="bucket" faceKey={b} nullAs="key" width={ARCHIVE_TAB_SHIMMER_WIDTH[b]} height={16} render={w => w} />
                  </span>
                  <span style={{ flexShrink: 0 }}>({count})</span>
                </button>
              );
            })}
          </div>

          {/* Column headers */}
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: `${theme.spacing.xs}px ${theme.spacing.md}px ${theme.spacing.xs}px ${4 + theme.spacing.sm}px`,
            fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary,
            textTransform: 'uppercase', letterSpacing: 0.5,
            borderBottom: `1px solid ${theme.colors.borderLight}`,
            userSelect: 'none',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>Betreff / {cm.categoryNoun}</div>
            <div style={{ width: bucket === 'liegengeblieben' ? 80 : 28, flexShrink: 0, textAlign: bucket === 'liegengeblieben' ? 'right' : 'center' }}>
              {bucket === 'liegengeblieben' ? 'Aktionen' : ''}
            </div>
          </div>

          {/* Rows */}
          <div ref={listRef} tabIndex={-1} style={{ flex: 1, overflowY: 'auto', outline: 'none' }}>
            {/* The SETTLE HOLD (round-3 review, controller-ruled). App seeds strip_label before
                /config answers, so a first uncached load painted badge rows and then grew a 32px
                axis column under them when the real mode landed — every row sliding sideways at
                once. The ledger therefore waits for config to decide before it paints a row at
                all, the same posture the draft area takes for its own order-dependent canvas.
                Pre-allocating the column instead was ruled OUT: it would leave a 32px gutter on
                every strip_label row forever, which is not the shape the ruling describes. */}
            {archived.loading || cm.configLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
                Lade Archiv...
              </div>
            ) : archived.error ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.error, fontSize: 13 }}>
                {archived.error}
                <div style={{ marginTop: theme.spacing.sm }}>
                  <button
                    onClick={() => archived.reload()}
                    style={{
                      padding: '6px 14px', fontSize: 13, fontWeight: 500,
                      fontFamily: theme.font.family,
                      background: 'transparent', color: theme.colors.navy,
                      border: `1px solid ${theme.colors.borderLight}`,
                      borderRadius: theme.borderRadius.sm, cursor: 'pointer',
                    }}
                  >Erneut versuchen</button>
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 13 }}>
                {hasActiveFilters ? 'Keine Treffer' : 'Keine archivierten Tickets'}
              </div>
            ) : (
              rows.map((t, idx) => {
                const isSelected = selectedUuid === t.uuid;
                const isPicked = sel.selected.has(t.uuid);
                const isRemoving = removingUuids.has(t.uuid);
                return (
                  <div
                    key={t.uuid}
                    data-uuid={t.uuid}
                    className={isRemoving ? 'stx-archiving' : undefined}
                    // Phase 2 follow-up — was role=button + aria-pressed, but the row
                    // contains the child Restore button which axe (correctly) flags as
                    // nested-interactive. The row stays keyboard-operable via tabIndex
                    // + onKeyDown; aria-label gives SR users the row's name.
                    tabIndex={0}
                    aria-label={`Archiviertes Ticket ${t.subject ?? t.uuid}`}
                    onClick={() => {
                      if (sel.mode) { sel.toggle(t.uuid); return; }
                      setSelectedUuid(isSelected ? null : t.uuid);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (sel.mode) { sel.toggle(t.uuid); return; }
                        const opening = !isSelected;
                        setSelectedUuid(isSelected ? null : t.uuid);
                        // Focus follows the KEYBOARD activation only (this is onKeyDown, never
                        // onClick — taking focus off a mouse user's pointer is the opposite of
                        // helpful), and only when a detail is actually opening. Deferred a frame so
                        // the pane has rendered the region before we focus it. Same rule, same
                        // region, as AntwortenSeite.
                        if (opening) requestAnimationFrame(() => detailRegionRef.current?.focus());
                      }
                    }}
                    style={{
                      display: 'flex', alignItems: 'center',
                      backgroundColor: (isPicked || isSelected) ? theme.colors.bgLight : (idx % 2 === 1 ? theme.colors.bgPage : 'transparent'),
                      borderLeft: isPicked ? `4px solid ${theme.colors.navy}` : '4px solid transparent',
                      borderBottom: `1px solid ${theme.colors.borderLight}`,
                      cursor: 'pointer',
                      opacity: restoringUuids.has(t.uuid) ? 0 : 1,
                      transition: 'opacity 0.3s ease, background-color 0.15s ease',
                      animation: 'stx-fadeIn 0.3s ease',
                      ...(isRemoving ? { transitionDelay: `${idx * 40}ms` } : {}),
                    }}
                  >
                    {/* The AXIS strip (operator ruling, walk fix round 2, 2026-09-09) — the active
                        list's own idiom, brought to the ledger and keyed on the SAME setting:
                        strip_abbr puts the Kürzel here and drops line 2's badge, strip_label keeps
                        the badge and renders no strip. Before this the ledger badged in every mode,
                        which put the served handling word on the row twice — badge and resolution
                        pill naming the same handling.
                        `alignSelf: stretch` is the whole adaptation: the column takes whatever
                        height the compact row has rather than declaring one of its own, so the
                        ledger's row height is untouched by its presence. Its WIDTH is fixed and
                        flexShrink-0, so a vocabulary landing later (the Kürzel arrives with it, and
                        is simply absent until then) cannot re-flow the row. */}
                    {/* NO HOVER on this strip (operator ruling 2026-09-16). It used to open the
                        house InfoTooltip for parity with the active list; on a compact ledger row
                        that parity turned into a bubble popping up on every pass of the pointer
                        down the column, on a surface that is read, not decided on. The active list
                        keeps its tooltip — a clerk there is choosing what to do — and whether the
                        two lists should converge again is an operator call on the triage surface,
                        not this fix's. */}
                    {axisStrip && (
                      <div
                        data-testid="archiv-axis-strip"
                        style={{
                          width: theme.stripWidth[cm.rowLayout], flexShrink: 0, alignSelf: 'stretch',
                          ...primaryBandStyle(cm, t.primaryAxis, theme.colors.borderLight, isSelected),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        {/* Shared face (shared.tsx) — served glyph, else served Kürzel, else
                            nothing. The strip's own box above is unchanged. */}
                        <HandlingStripFace value={t.primaryAxis ?? null} layout={cm.rowLayout} />
                      </div>
                    )}

                    {/* Content — the selection stripe at the row's left edge is a different stripe
                        (it says PICKED, not which axis) and is unaffected by any of the above. */}
                    <div style={{
                      flex: 1, display: 'flex', alignItems: 'center',
                      padding: `${theme.spacing.xs}px ${theme.spacing.md}px ${theme.spacing.xs + 6}px ${theme.spacing.sm}px`,
                      minWidth: 0,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Line 1: subject (muted) · the ONE CLOCK · archived date (right). The two
                            dates on a ledger row are DIFFERENT facts and stay that way: the ARRIVAL
                            is the same row-1 age the active list carries (ArrivalAge — part 7 fix
                            loop, 2026-10-12, operator ruling: once on screen, once on hover, the
                            subject carries no hover), and the ARCHIVE stamp beside it is what this
                            list sorts by. */}
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                          <div style={{
                            flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: theme.colors.navyMid,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          }}>
                            {t.subject || '(Kein Betreff)'}
                          </div>
                          <ArrivalAge receivedAt={t.receivedAt} createdAt={t.createdAt} flags={t.flags} />
                          <span style={{ flexShrink: 0, fontSize: 11, color: theme.colors.navyMuted }}>
                            {formatRowDate(t.archivedAt || t.updatedAt)}
                          </span>
                        </div>
                        {/* Line 2: shared compact meta — badge · reason · 📎 · status (right) */}
                        <TicketRowMeta ticket={t} maxSecondaryAxisVisible={SECONDARY_AXIS_MAX_VISIBLE.responses} compact attachmentsCountInline={attachmentsCountInline} />
                      </div>

                      {/* Per-row action: Wiederherstellen, on every bucket. Liegengeblieben also
                          carried an Erledigen (approve in place) until the round-5 ruling removed
                          that capability for all roles — with it went this column's second slot,
                          so the width is now the one-button width everywhere. */}
                      <div style={{
                        width: 28,
                        flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                        gap: 2,
                      }}>
                        <button
                          onClick={e => { e.stopPropagation(); handleRestore(t.uuid); }}
                          disabled={restoringUuids.has(t.uuid)}
                          aria-label="Wiederherstellen"
                          title="Wiederherstellen"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: theme.colors.navyMuted, fontSize: 14, padding: 2,
                            opacity: restoringUuids.has(t.uuid) ? 0.3 : 0.6,
                            transition: 'opacity 0.15s ease',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; }}
                        >
                          {/* Restore: the archive box with the arrow coming out (the Auto button's Archiv face). */}
                          <RestoreIcon size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {/* Infinite-scroll sentinel — observed only once mounted; loadMore
                self-guards on nextCursor + in-flight. Kept inside the scroll
                container so IntersectionObserver uses it as the implicit root. */}
            {!archived.loading && !archived.error && rows.length > 0 && (
              <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />
            )}
            {archived.loadingMore && (
              <div style={{ padding: 16, textAlign: 'center', color: theme.colors.textSecondary, fontSize: 12 }}>
                Lade weitere…
              </div>
            )}
          </div>
        </BentoCard>

        {/* Right: detail panel */}
        <BentoCard
          title="Details"
          headerAction={selectedUuid ? (
            <div style={{ display: 'flex', gap: 4, animation: 'stx-fadeIn 0.2s ease' }}>
              <HeaderIconButton label="In eigenem Fenster öffnen" onClick={popoutDetail}><PopoutIcon size={15} /></HeaderIconButton>
              <HeaderIconButton label="Auswahl aufheben" onClick={closeDetail}><CircleIcon size={16} /></HeaderIconButton>
            </div>
          ) : undefined}
          style={{
            display: 'flex', flexDirection: 'column', minWidth: 0,
            ...(isCompact
              ? { width: '100%', flexShrink: 0, minHeight: 300 }
              : { flex: ratio.detail, transition: MORPH_TRANSITION }),
          }}
          contentStyle={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', padding: theme.spacing.md }}
        >
          {/* THE focus target for a keyboard activation, mounted in EVERY state (placeholder and
              detail alike) so the ref is stable and the pane's geometry never depends on which
              state is showing — the same landmark, with the same name, as Antworten's. */}
          <div
            ref={detailRegionRef}
            role="region"
            aria-label="Ticketdetails"
            tabIndex={-1}
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', outline: 'none' }}
          >
          {selectedUuid ? (
            <TicketDetailComponent
              onInfo={setInfoToast}
              ticket={selectedTicket!}
              loading={detailLoading}
              actionLoading={actionLoading}
              error={error}
              onClearError={() => setError(null)}
              onApprove={() => {}}
              onReject={() => {}}
              // The letter stays reachable from the Archiv (slot-matrix proposal, 2026-10-23): an
              // Erledigt row approved with a letter downloads it beside Wiederherstellen.
              onDownload={async () => {
                try { await smarttix.downloadDraft(selectedUuid); }
                catch (err: any) { setError(formatBackendError(err, 'Herunterladen des Entwurfs')); }
              }}
              onRestore={() => handleRestore(selectedUuid)}
              // Kopieren on past letters, in-page as in the pop-out (Q3 A, 2026-09-17).
              onCopyDraft={handleCopyDraft}
              // Whatever the detail could not complete is stated in THIS pane's banner, the same
              // one `error` above already feeds — without it a failure inside the detail had
              // nowhere to go on this page.
              onActionError={setError}
              piiIgnoreList={piiIgnoreList}
              onPiiIgnore={async (fieldType, value) => {
                await smarttix.addPiiIgnore(fieldType, value);
                setPiiIgnoreList(prev => [...prev, { id: 0, fieldType, value }]);
              }}
              showSourceChannel={showSourceChannel}
              attachmentsCountInline={attachmentsCountInline}
            />
          ) : (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.colors.textSecondary, fontSize: 13,
            }}>
              Ticket auswählen
            </div>
          )}
          </div>
        </BentoCard>
      </div>

      {/* The restore confirmation, through THE toast component (2026-09-08, walk fix round 4).
          It was a page-local copy of the same bottom-centred navy card — same intent, its own
          tokens and its own timer, which is how two toasts on one screen end up looking different.
          Info mode: a restore has no undo of its own; the row is back in the list. */}
      <RueckgaengigToast
        visible={!!toast}
        message={toast ?? ''}
        durationMs={3000}
        onDismiss={() => setToast(null)}
      />

      <RueckgaengigToast
        visible={!!undoToast}
        message={undoToast ? (
          <><CountUpNumber target={undoToast.count} /> Ticket{undoToast.count !== 1 ? 's' : ''} {undoToast.verb}</>
        ) : ''}
        onUndo={undoToast?.undo === 'archive' ? handleAutoUndo : undefined}
        onDismiss={() => setUndoToast(null)}
      />

      <RueckgaengigToast
        visible={!!infoToast}
        message={infoToast ?? ''}
        onDismiss={() => setInfoToast(null)}
      />

    </div>
  );
};

export default ArchivSeite;
